// Centralized API service for all API calls

const GEOLOCATION_API = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';
const LEAD_API = 'https://leadapi.crmbytes.com/api/Lead/AddLead';

/**
 * Fetch geolocation data based on user's IP
 */
export const getGeoLocation = async () => {
  try {
    const response = await fetch(GEOLOCATION_API, {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });

    const data = await response.json();
    
    if (data.isSuccess) {
      return {
        success: true,
        data: data.data
      };
    }
    
    return {
      success: false,
      error: 'Failed to fetch geolocation'
    };
  } catch (error) {
    console.error('Geolocation API error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Extract country dial code from country code
 */
const getDialCodeFromCountryCode = (countryCode) => {
  const countryDialCodes = {
    'US': 1, 'CA': 1, 'GB': 44, 'IN': 91, 'CN': 86, 'JP': 81, 
    'DE': 49, 'FR': 33, 'IT': 39, 'AU': 61, 'AE': 971, 'PK': 92,
    'NL': 31, 'ES': 34, 'BR': 55, 'MX': 52, 'RU': 7, 'ZA': 27,
    'KR': 82, 'SA': 966, 'TR': 90, 'ID': 62, 'MY': 60, 'SG': 65,
    'PH': 63, 'TH': 66, 'VN': 84, 'EG': 20, 'NG': 234
  };
  
  return countryDialCodes[countryCode] || 1; // Default to 1 if not found
};

/**
 * Submit lead form
 * @param {Object} formData - Form data from the contact form
 * @param {Object} geoData - Geolocation data (optional)
 */
export const submitLead = async (formData, geoData = null) => {
  try {
    // Get current page URL
    const pageUrl = window.location.href;
    
    // Prepare payload
    const payload = {
      name: formData.name,
      email: formData.email,
      countryDialCode: parseInt(formData.countryCode.replace('+', '')),
      country: geoData?.countryCode || 'US',
      phoneNumber: formData.phone, // Only numeric digits
      ipAddress: geoData?.ipAddress || '',
      description: formData.message,
      pageUrl: pageUrl,
      city: geoData?.cityName || '',
      state: geoData?.regionName || '',
      geoLocation: geoData ? JSON.stringify({ 
        isSuccess: true, 
        data: geoData,
        errorMessage: "",
        statusCode: 200 
      }) : '',
      packageTitle: '',
      packageAmount: 0
    };

    const response = await fetch(LEAD_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'text/plain'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    // Handle different status codes
    if (response.status === 200 && data.isSuccess && data.data?.customerId) {
      return {
        success: true,
        customerId: data.data.customerId,
        message: data.data.message
      };
    } else if (response.status === 200 && data.isSuccess) {
      return {
        success: true,
        message: data.data.message
      };
    } else if (response.status === 401) {
      return {
        success: false,
        error: data.message || 'Unauthorized access'
      };
    } else if (response.status === 400) {
      return {
        success: false,
        error: data.errorMessage || data.data?.message || 'Invalid data provided'
      };
    } else {
      return {
        success: false,
        error: 'An unexpected error occurred'
      };
    }
  } catch (error) {
    console.error('Lead submission error:', error);
    return {
      success: false,
      error: 'Failed to submit form. Please try again.'
    };
  }
};

/**
 * Get country code mapping for the country selector
 */
export const getCountryFromDialCode = (dialCode) => {
  const dialCodeToCountry = {
    '+1': 'US',
    '+44': 'GB',
    '+91': 'IN',
    '+86': 'CN',
    '+81': 'JP',
    '+49': 'DE',
    '+33': 'FR',
    '+39': 'IT',
    '+61': 'AU',
    '+971': 'AE',
    '+92': 'PK',
    '+31': 'NL',
    '+34': 'ES',
    '+55': 'BR',
    '+52': 'MX',
    '+7': 'RU',
    '+27': 'ZA',
    '+82': 'KR',
    '+966': 'SA',
    '+90': 'TR',
    '+62': 'ID',
    '+60': 'MY',
    '+65': 'SG',
    '+63': 'PH',
    '+66': 'TH',
    '+84': 'VN',
    '+20': 'EG',
    '+234': 'NG'
  };
  
  return dialCodeToCountry[dialCode] || 'US';
};
